/**
 * Shared sanitization helpers used across the widget render path
 * (both the legacy imperative DOM build in `widget.ts` and the new
 * Preact components in `components/`).
 *
 * SECURITY CONTRACT
 * -----------------
 * - `sanitizeUrl` returns an empty string for any URL that is NOT
 *   an absolute http(s) URL. Empty string is the signal callers use
 *   to render a placeholder instead.
 * - `escapeHtml` and `escapeAttribute` are kept for the legacy
 *   markdown-fallback path. Preact components MUST NOT call them on
 *   text they interpolate via JSX — Preact escapes by default.
 */

export function sanitizeUrl(value: string | null | undefined): string {
  const trimmed = String(value || '').trim();
  if (!trimmed) return '';

  try {
    const base =
      typeof window !== 'undefined' && window.location
        ? window.location.origin
        : 'http://localhost';
    const parsed = new URL(trimmed, base);
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
      return '';
    }
    return trimmed;
  } catch {
    return '';
  }
}

export function escapeHtml(value: string): string {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

export function escapeAttribute(value: string): string {
  return escapeHtml(value).replace(/`/g, '&#96;');
}

export function decodeHtmlEntities(value: string): string {
  return String(value)
    .replace(/&amp;/g, '&')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>');
}
